import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Users, Search, Crown, Award, Coins, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import PageHeader from '@/components/PageHeader'
import { playersAPI } from '@/lib/api'
import { Avatar } from '@/components/profile/ProfileFrame'

/* Spelerslijst — wie doet er mee, en wat hebben ze op hun naam staan.
   Gesorteerd op toernooiwinsten, zodat de vaste winnaars bovenaan staan. */

export default function PlayersPage() {
  const [players, setPlayers] = useState([])
  const [q, setQ]             = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const load = useCallback((query) => {
    playersAPI.list(query)
      .then(r => setPlayers(r.data?.players ?? []))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load('') }, [load])

  // Debounce zodat we niet bij elke toetsaanslag de server raken.
  useEffect(() => {
    const t = setTimeout(() => load(q), 250)
    return () => clearTimeout(t)
  }, [q, load])

  return (
    <div>
      <PageHeader
        icon={Users}
        title="Spelers"
        subtitle="Iedereen die meedoet, met wat ze op hun naam hebben staan"
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)}
               placeholder="Zoek op naam…" className="pl-9" />
      </div>

      {loading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error   && <p className="text-sm text-signal">{error}</p>}

      {!loading && players.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {q ? `Niemand gevonden voor "${q}".` : 'Nog geen spelers.'}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {players.map(p => <PlayerCard key={p.id} player={p} />)}
      </div>
    </div>
  )
}

function PlayerCard({ player }) {
  const name = player.display_name || player.username
  const wins = Number(player.tournaments_won || 0)

  return (
    <Link to={`/spelers/${player.id}`} className="block no-underline">
      <Card className="transition-colors hover:border-acid h-full"
            style={wins > 0 ? { borderLeft: '2px solid #E4C265' } : undefined}>
        <CardContent className="p-3 flex items-center gap-3">
          <Avatar url={player.avatar_url} name={name} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{name}</div>
            <div className="flex items-center gap-3 mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {wins > 0 && (
                <span className="inline-flex items-center gap-1 text-gold">
                  <Crown className="h-3 w-3" />{wins}
                </span>
              )}
              {Number(player.badge_count) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Award className="h-3 w-3" />{player.badge_count}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Coins className="h-3 w-3" />{player.points ?? 0}
              </span>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardContent>
      </Card>
    </Link>
  )
}
